import { useEffect, useRef, useState } from 'react'
import { useBriefingDoc, useUpdateBriefingDoc } from '../hooks/useBriefingDoc'
import { useEvent } from '../hooks/useEvents'
import { usePresentationTimetable, useServiceInfo } from '../hooks/useServices'
import { useBriefingTickets } from '../hooks/useTicketTypes'
import { ApiClient } from '../api'
import { MarkdownEditor } from './shared/MarkdownEditor'
import { Field, inputClass, ServerError } from '../components/modal-form'
import { Icon } from '../components/Icon'
import { useArchived } from '../contexts/ArchiveContext'
import { formatGBP } from '../lib/money'
import { formatEventDates } from '../lib/dates'
import type { EventBriefingDoc, EventListItem, EventServiceInfo, PresTimetableRow, BriefingTicket } from '../types'

// Build contextual starter content for the prose sections from data the system
// already holds (event facts, timetable, ticket prices). Used by the "Generate
// starter draft" button, which fills only EMPTY boxes so it never clobbers
// human edits. These are drafts to review + edit, not final copy. The FAQ is
// generated separately from the managed FAQ library (see "Generate FAQ").
function buildStarters(
  event: EventListItem,
  services: PresTimetableRow[],
  info: EventServiceInfo | null,
  tickets: BriefingTicket[]
): Partial<EventBriefingDoc> {
  const evServices = services.filter((s) => s.serviceType !== 'pullman' && s.serviceType !== 'dining')
  // Distinct departure times (MERAC events project one row per day — collapse
  // to the pattern for the prose blocks).
  const times = Array.from(new Set(evServices.map((s) => s.timeText).filter(Boolean))).sort()
  const firstOrigin = evServices.find((s) => s.originStation)?.originStation ?? event.location ?? 'the station'
  const priced = tickets.filter((t) => t.pricePence != null)
  const when = formatEventDates(event.dates, event.startDate, event.endDate)
  const priceList = priced.map((t) => `${t.name} — ${formatGBP(t.pricePence!)}`).join('; ')
  const fromPrice = priced.length > 0 ? formatGBP(Math.min(...priced.map((t) => t.pricePence!))) : null

  // Smart summary block (item 7) — a genuinely useful overview built from the
  // event's own facts, not a generic stub.
  const summaryLines = [
    `**${event.name}** is a ${event.type.name}${event.location ? ` at ${event.location}` : ' on the North Yorkshire Moors Railway'}, running ${when}.`,
    '',
    `- **When:** ${when}`,
    `- **Where:** ${event.location ?? '_TBC_'}`,
    times.length ? `- **Departures:** ${times.join(', ')}` : null,
    fromPrice ? `- **Tickets from:** ${fromPrice}` : null,
    event.capacity != null ? `- **Capacity:** ${event.capacity.toLocaleString('en-GB')}` : null,
    event.description ? `\n${event.description.trim()}` : '\n_Add a short overview of the experience guests can expect._'
  ].filter((l) => l !== null)

  return {
    // Hero tagline defaults to a smart lead built from the event (item 5) — the
    // event name is already the cover H1; the dates show on the cover subline.
    heroTagline: event.description
      ? event.description.trim().split(/(?<=[.!?])\s/)[0]!.slice(0, 160)
      : `A ${event.type.name} on the North Yorkshire Moors Railway`,
    subtitle: 'Ticketing & Marketing Teams',
    overviewMd: summaryLines.join('\n'),
    conceptMd: `### The atmosphere\n\n_Describe the look, feel and hooks of the event._\n\n### Theme\n\n_${event.type.name} — describe the creative angle and what makes it shareable._`,
    guestJourneyMd: evServices.length
      ? [
          `1. Guests arrive at ${firstOrigin} for boarding.`,
          times.length ? `2. Services depart at ${times.join(', ')}.` : `2. Services depart per the timetable.`,
          `3. The train travels ${info?.journey ?? 'along the line'}${info?.runTime ? ` — approximately ${info.runTime}` : ''}.`,
          `4. Guests return to ${firstOrigin}.`
        ].join('\n')
      : '1. Guests arrive for boarding.\n2. _Describe the on-board experience._\n3. Guests return.',
    marketingMd: `### Audience & positioning\n\n_Who is this for, and how is it positioned?_\n\n### Key hooks\n\n- _Hook 1_\n- _Hook 2_`,
    ticketingNotesMd: [
      times.length ? `- **Departures:** ${times.join(', ')}` : null,
      `- **Boarding location:** ${event.location ?? 'see the timetable'}`,
      priceList ? `- **Ticket prices:** ${priceList}` : null,
      `- Confirmation emails should include the departure time, boarding location, access notes and any dress-code/theme note.`,
      `- Flag accessibility requirements at the point of sale.`
    ].filter(Boolean).join('\n')
    // faqMd intentionally omitted — generated from the managed FAQ library via
    // the dedicated "Generate FAQ" button.
  }
}

// ProductBriefingTab (Feature 1) — authors the rich, branded "Product Briefing"
// (like "A Bloody Merry Christmas Briefing"). Per-section markdown editors with
// live preview; schedule / capacity / ticket-pricing tables are pulled live
// into the printed PDF from the event's timetable + ticketing, so they aren't
// edited here. "Open PDF" opens the browser-print doc.

interface Props { eventId: number }

const BLANK: EventBriefingDoc = {
  heroTagline: null, subtitle: null, overviewMd: null, conceptMd: null,
  guestJourneyMd: null, marketingMd: null, ticketingNotesMd: null, faqMd: null
}

const SECTIONS: { key: keyof EventBriefingDoc; label: string; hint: string; placeholder: string }[] = [
  { key: 'overviewMd', label: 'Event overview', hint: 'What the event is, the headline experience.', placeholder: 'A uniquely immersive festive experience aboard the NYMR…' },
  { key: 'conceptMd', label: 'Concept & theme', hint: 'Atmosphere, hooks, the creative angle.', placeholder: 'Gothic Christmas meets Victorian ghost story…' },
  { key: 'guestJourneyMd', label: 'Guest journey', hint: 'Step-by-step, arrival to departure.', placeholder: '- Guests arrive at Pickering to a live platform performance…' },
  { key: 'marketingMd', label: 'Marketing notes', hint: 'Audience, positioning, key hooks.', placeholder: 'Primary audience: adults 25–45, couples, work parties…' },
  { key: 'ticketingNotesMd', label: 'Ticketing notes', hint: 'Confirmation email content, age policy, access.', placeholder: 'Confirmation emails should include departure time, boarding location…' },
  { key: 'faqMd', label: 'FAQ', hint: 'Guest questions & answers. Use "Generate FAQ" to build from the standard library.', placeholder: '### What is the event?\nA one-of-a-kind…' }
]

export function ProductBriefingTab({ eventId }: Props) {
  const archived = useArchived()
  const { data, isLoading, error } = useBriefingDoc(eventId)
  const updateMut = useUpdateBriefingDoc(eventId)
  const { data: event } = useEvent(eventId)
  const { data: timetable } = usePresentationTimetable(eventId)
  const { data: info } = useServiceInfo(eventId)
  const { data: tickets } = useBriefingTickets(eventId)
  const [form, setForm] = useState<EventBriefingDoc>(BLANK)
  const [dirty, setDirty] = useState(false)
  const [genNote, setGenNote] = useState<string | null>(null)
  const [faqBusy, setFaqBusy] = useState(false)

  // Hydrate the form from server data ONCE per event. Guarding on a ref (rather
  // than firing on every `data` reference) stops a background refetch — e.g.
  // React Query's refetchOnReconnect after a network blip — from silently
  // discarding the user's unsaved, in-progress edits.
  const hydratedFor = useRef<number | null>(null)
  useEffect(() => {
    if (data && hydratedFor.current !== eventId) {
      setForm(data)
      setDirty(false)
      hydratedFor.current = eventId
    }
  }, [data, eventId])

  function set<K extends keyof EventBriefingDoc>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value === '' ? null : value }))
    setDirty(true)
  }

  function handleSave() {
    updateMut.mutate(form, { onSuccess: () => setDirty(false) })
  }

  // Fill ONLY the empty boxes with contextual starters built from event data
  // (never overwrites human-written content), then PERSIST immediately so the
  // generated draft survives navigating away (it previously lived only in local
  // state and vanished on tab change).
  function generateDraft() {
    if (!event) return
    const starters = buildStarters(event, timetable?.services ?? [], info ?? null, tickets ?? [])
    const next = { ...form }
    let filled = 0
    for (const k of Object.keys(starters) as (keyof EventBriefingDoc)[]) {
      const cur = next[k]
      if ((cur == null || cur === '') && starters[k]) {
        next[k] = starters[k]!
        filled++
      }
    }
    if (filled > 0) {
      setForm(next)
      updateMut.mutate(next, { onSuccess: () => setDirty(false) })
    }
    setGenNote(filled > 0
      ? `Filled and saved ${filled} empty section${filled === 1 ? '' : 's'} from event data. Review and edit as needed.`
      : 'Nothing to fill — every section already has content.')
  }

  // Generate the FAQ from the managed guest FAQ library (standard answers with
  // live prices / departures / dates injected), then persist. Regeneration
  // replaces the current FAQ, so confirm when there's content to lose.
  async function generateFaq() {
    if (faqBusy) return
    if ((form.faqMd ?? '').trim().length > 0 &&
        !window.confirm('Replace the current FAQ with a freshly generated one from the standard library?')) {
      return
    }
    setFaqBusy(true)
    setGenNote(null)
    try {
      const { markdown, topicCount } = await ApiClient.faqSuggestion(eventId)
      if (!markdown.trim()) {
        setGenNote('No FAQ topics are marked in the standard library yet — add some under Resources → Briefing topics.')
        return
      }
      const next = { ...form, faqMd: markdown }
      setForm(next)
      updateMut.mutate(next, { onSuccess: () => setDirty(false) })
      setGenNote(`Generated the FAQ from ${topicCount} standard topic${topicCount === 1 ? '' : 's'} and saved. Review and edit as needed.`)
    } catch (err) {
      setGenNote(`Could not generate the FAQ: ${(err as Error).message}`)
    } finally {
      setFaqBusy(false)
    }
  }

  if (isLoading) return <div className="text-ink-soft text-sm font-mono">Loading briefing…</div>
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load briefing</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl">Product Briefing</h2>
          <p className="text-sm text-ink-soft max-w-2xl">
            The complete product briefing for ticketing &amp; marketing. Schedule,
            capacity and ticket prices are pulled automatically from this event's
            timetable and ticketing into the PDF.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`/events/${eventId}/product-briefing`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors inline-flex items-center gap-1.5"
          >
            <Icon name="download" size={14} /> Open PDF
          </a>
          {!archived && (
            <button
              type="button"
              onClick={generateDraft}
              disabled={!event}
              title="Fill any empty sections with a starter draft built from this event's details, timetable and ticket prices, and save it."
              className="px-3 py-1.5 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors disabled:opacity-50"
            >
              Generate starter draft
            </button>
          )}
          {!archived && (
            <button
              type="button"
              onClick={generateFaq}
              disabled={faqBusy}
              title="Build the FAQ from the standard guest-FAQ library, with this event's dates, departures and ticket prices injected, and save it."
              className="px-3 py-1.5 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors disabled:opacity-50"
            >
              {faqBusy ? 'Generating…' : 'Generate FAQ'}
            </button>
          )}
          {!archived && (
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || updateMut.isPending}
              className="px-3 py-1.5 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
            >
              {updateMut.isPending ? 'Saving…' : dirty ? 'Save' : 'Saved'}
            </button>
          )}
        </div>
      </div>

      {genNote && (
        <div className="bg-green-soft/50 border border-green/40 rounded p-2.5 text-sm text-green">{genNote}</div>
      )}

      {archived && (
        <div className="bg-parchment border border-line rounded p-3 text-sm text-ink-soft">
          This event is archived — the briefing is read-only. Open the PDF to view or print it.
        </div>
      )}

      <div className="bg-white border border-line rounded p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Hero tagline" hint="The line under the title on the cover.">
          <input className={inputClass(false)} value={form.heroTagline ?? ''} maxLength={200} onChange={(e) => set('heroTagline', e.target.value)} placeholder="A festive experience on the North Yorkshire Moors Railway" />
        </Field>
        <Field label="Subtitle" hint="Optional second line.">
          <input className={inputClass(false)} value={form.subtitle ?? ''} maxLength={200} onChange={(e) => set('subtitle', e.target.value)} placeholder="Ticketing & Marketing Teams" />
        </Field>
      </div>

      {SECTIONS.map((s) => (
        <div key={s.key} className="bg-white border border-line rounded p-4">
          <Field label={s.label} hint={s.hint}>
            <MarkdownEditor
              value={(form[s.key] as string | null) ?? ''}
              onChange={(v) => set(s.key, v)}
              placeholder={s.placeholder}
              preview="tab"
              minHeight={160}
            />
          </Field>
        </div>
      ))}

      {updateMut.error && <ServerError title="Could not save briefing" error={updateMut.error as Error} />}

      {!archived && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || updateMut.isPending}
            className="px-4 py-2 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
          >
            {updateMut.isPending ? 'Saving…' : dirty ? 'Save briefing' : 'Saved'}
          </button>
        </div>
      )}
    </div>
  )
}
